import { ValidatorFn, Validators } from '@angular/forms';
import moment from 'moment';
import { UTC_EDP_INPUT_FORMAT } from '../../shared/constants/date.const';
import {
  isNotNullOrEmptyString,
  sanitizeValueOr,
  generateRateRange,
  getUserRatePrefs,
} from '../../shared/utils/functions';
import { SpaceCardDetails } from '../../stacking-plan-container/actions-toolbar/settings/settings.model';
import { EDPSpaceInfo, SuiteType } from '../../stacking-plan-container/floor-container/floor/item/item.model';
import { MappedLov } from '../../stacking-plan.model';
import { RepresentativeClass } from '../reps/representative.class';
import { AbstractSpaceClass, SpaceClassOptions } from './abstract-space.class';

export enum AvailabilityType {
  InvestmentSale = 'Inversment Sale',
  Lease = 'Lease',
  NotUsable = 'Not Usable',
  Sale = 'Sale',
  SaleOrLease = 'Sale or Lease',
  Unknown = 'Unknown',
  UserSale = 'User Sale',
}

enum SpaceAvailabilityStatusEnum {
  Active = 'Active',
}

export class AvailabilitySpaceClass extends AbstractSpaceClass {
  id: number;
  activeF: boolean;
  availabilityType: string;
  propertyUsageId: number;
  refPropertyUsageTypeDesc: string;
  refPropertyUsageSubTypeDesc: string;
  dateAvailable: string; // TODO: use moment lib
  dateOnMarket: string; // TODO: use moment lib
  edpLastUpdateDate: string; // TODO: use moment lib
  edpLastUpdateBy: string;
  askingRentalRateYearly: number;
  askingRentalRateYearlyUomDesc: string;
  totalAreaOfSpace: number;
  totalAreaOfSpaceUomDesc: string;
  fullFloorF: boolean;
  vacantF: boolean;
  listingNotes: string;
  listingReps: RepresentativeClass[];
  miqRecordHideF: boolean;
  verified_ts: string | Date;
  minAskingRateYr: number;
  minAskingRateYrUom: string;
  minAskingRateMo: number;
  minAskingRateMoUom: string;
  maxAskingRateYr: number;
  maxAskingRateYrUom: string;
  maxAskingRateMo: number;
  maxAskingRateMoUom: string;
  spaceAvailabilityStatus: string;
  leaseType: string;
  possession: string;
  estimatedAskingRentF: boolean;
  minDivisibleSpace: number;
  minDivisibleSpaceUom: string;
  contiguousArea: number;
  contiguousAreaUom: string;

  /**
   * Review if leasing rent rate & UOM exists and return the concatenated value.
   * Return only the leasing rent rate if UOM is not present, else returns null.
   */

  constructor(edpSpaceInfo: EDPSpaceInfo, options?: Partial<SpaceClassOptions>) {
    super(edpSpaceInfo, SuiteType.Availability, options);
    const {
      id,
      active_f,
      availability_type,
      property_usage_id,
      ref_property_usage_type_desc,
      ref_property_usage_sub_type_desc,
      date_available,
      date_on_market,
      edp_last_update_date,
      edp_last_update_by,
      asking_rental_rate_yearly,
      asking_rental_rate_yearly_uom_desc,
      full_floor_f,
      vacant_f,
      listing_notes,
      listing_reps,
      available_space,
      available_space_uom_desc,
      miq_record_hide_f,
      verified_ts,
      minimum_asking_rate_monthly,
      minimum_asking_rate_monthly_uom,
      maximum_asking_rate_monthly,
      maximum_asking_rate_monthly_uom,
      minimum_asking_rate_yearly,
      minimum_asking_rate_yearly_uom,
      maximum_asking_rate_yearly,
      maximum_asking_rate_yearly_uom,
      ref_space_availability_status_desc,
      ref_lease_type_desc,
      ref_possession_type_desc,
      estimated_asking_rent_f,
      total_contiguous_area_of_space,
      total_contiguous_area_of_space_uom_desc,
      minimum_divisible_surface_area,
      minimum_divisible_surface_area_uom_desc,
    } = edpSpaceInfo.availability;
    this.id = id;
    this.activeF = active_f;
    this.availabilityType = availability_type;
    this.propertyUsageId = property_usage_id;
    this.refPropertyUsageTypeDesc = ref_property_usage_type_desc;
    this.refPropertyUsageSubTypeDesc = ref_property_usage_sub_type_desc;
    this.dateAvailable = date_available;
    this.dateOnMarket = date_on_market;
    this.edpLastUpdateDate = edp_last_update_date;
    this.edpLastUpdateBy = edp_last_update_by;
    this.askingRentalRateYearly = asking_rental_rate_yearly;
    this.askingRentalRateYearlyUomDesc = asking_rental_rate_yearly_uom_desc;
    this.totalAreaOfSpace = available_space;
    this.totalAreaOfSpaceUomDesc = available_space_uom_desc;
    this.fullFloorF = full_floor_f;
    this.vacantF = vacant_f;
    this.listingNotes = listing_notes;
    this.listingReps = RepresentativeClass.fromEDPRepresentativeInfoArray(listing_reps, this.options);
    this.miqRecordHideF = miq_record_hide_f;
    this.verified_ts = verified_ts;
    this.minAskingRateMo = minimum_asking_rate_monthly;
    this.minAskingRateMoUom = minimum_asking_rate_monthly_uom;
    this.maxAskingRateMo = maximum_asking_rate_monthly;
    this.maxAskingRateMoUom = maximum_asking_rate_monthly_uom;
    this.minAskingRateYr = minimum_asking_rate_yearly;
    this.minAskingRateYrUom = minimum_asking_rate_yearly_uom;
    this.maxAskingRateYr = maximum_asking_rate_yearly;
    this.maxAskingRateYrUom = maximum_asking_rate_yearly_uom;
    this.spaceAvailabilityStatus = ref_space_availability_status_desc;
    this.leaseType = ref_lease_type_desc;
    this.possession = ref_possession_type_desc;
    this.estimatedAskingRentF = estimated_asking_rent_f;
    this.contiguousArea = total_contiguous_area_of_space;
    this.contiguousAreaUom = total_contiguous_area_of_space_uom_desc;
    this.minDivisibleSpace = minimum_divisible_surface_area;
    this.minDivisibleSpaceUom = minimum_divisible_surface_area_uom_desc;
  }

  getAreaSpace(): number {
    return this.totalAreaOfSpace;
  }

  getAreaSpaceUOM(): string {
    return this.totalAreaOfSpaceUomDesc;
  }

  generateRentRateValue(): number {
    return this.askingRentalRateYearly;
  }

  generateRentRateUOM(): string {
    return this.askingRentalRateYearlyUomDesc;
  }

  getEstimatedAskingRent(): boolean {
    return this.estimatedAskingRentF;
  }
  generateMinMonthlyRent(): number {
    return this.minAskingRateMo;
  }

  generateMinMonthlyRentUOM(): string {
    return this.minAskingRateMoUom;
  }

  generateMaxMonthlyRentUOM(): string {
    return this.maxAskingRateMoUom;
  }
  generateMaxMonthlyRent(): number {
    return this.maxAskingRateMo;
  }
  generateMaxYearlyRentUOM(): string {
    return this.maxAskingRateYrUom;
  }
  generateMaxYearlyRent(): number {
    return this.maxAskingRateYr;
  }

  generateMinYearlyRentUOM(): string {
    return this.minAskingRateYrUom;
  }
  generateMinYearlyRent(): number {
    return this.minAskingRateYr;
  }
  generateMinDivisibleSpaceUOM(): string {
    return this.minDivisibleSpaceUom;
  }
  generateMinDivisibleSpace(): number {
    return this.minDivisibleSpace;
  }
  generateMaxContigousUOM(): string {
    return this.contiguousAreaUom;
  }
  generateMaxContiguous(): number {
    return this.contiguousArea;
  }

  internalSpaceInfoClone(): Pick<EDPSpaceInfo, 'tenant' | 'lease' | 'availability' | 'non_rentable_space'> {
    const listingReps =
      this.listingReps != null && Array.isArray(this.listingReps)
        ? this.listingReps.map(listingRep => listingRep.toEDPRepresentativeInfo())
        : null;
    return {
      tenant: null,
      lease: null,
      availability: {
        id: this.id,
        active_f: this.activeF,
        availability_type: this.availabilityType,
        property_usage_id: this.propertyUsageId,
        ref_property_usage_type_desc: this.refPropertyUsageTypeDesc,
        ref_property_usage_sub_type_desc: this.refPropertyUsageSubTypeDesc,
        date_available: this.dateAvailable,
        date_on_market: this.dateOnMarket,
        edp_last_update_date: this.edpLastUpdateDate,
        edp_last_update_by: this.edpLastUpdateBy,
        asking_rental_rate_yearly: this.askingRentalRateYearly,
        asking_rental_rate_yearly_uom_desc: this.askingRentalRateYearlyUomDesc,
        full_floor_f: this.fullFloorF,
        vacant_f: this.vacantF,
        listing_notes: this.listingNotes,
        listing_reps: listingReps,
        available_space: this.totalAreaOfSpace,
        available_space_uom_desc: this.totalAreaOfSpaceUomDesc,
        // TODO: review following mapping
        total_area_of_space: this.totalAreaOfSpace,
        total_area_of_space_uom_desc: this.totalAreaOfSpaceUomDesc,
        miq_record_hide_f: this.miqRecordHideF,
        verified_ts: this.verified_ts,
        minimum_asking_rate_monthly: this.minAskingRateMo,
        minimum_asking_rate_monthly_uom: this.minAskingRateMoUom,
        maximum_asking_rate_monthly: this.maxAskingRateMo,
        maximum_asking_rate_monthly_uom: this.maxAskingRateMoUom,
        minimum_asking_rate_yearly: this.minAskingRateYr,
        minimum_asking_rate_yearly_uom: this.minAskingRateYrUom,
        maximum_asking_rate_yearly: this.maxAskingRateYr,
        maximum_asking_rate_yearly_uom: this.maxAskingRateYrUom,
        ref_space_availability_status_desc: this.spaceAvailabilityStatus,
        ref_lease_type_desc: this.leaseType,
        ref_possession_type_desc: this.possession,
        estimated_asking_rent_f: this.estimatedAskingRentF,
        minimum_divisible_surface_area: this.minDivisibleSpace,
        minimum_divisible_surface_area_uom_desc: this.minDivisibleSpaceUom,
        total_contiguous_area_of_space: this.contiguousArea,
        total_contiguous_area_of_space_uom_desc: this.contiguousAreaUom,
      },
      non_rentable_space: null,
    };
  }

  generateRentRate(userSelection?: string, detailsToDisplay?: SpaceCardDetails): string {
    if (!userSelection) {
      return '-';
    }
    const isMonthly = userSelection === 'monthly';
    const rateObject = isMonthly
      ? {
          minAskingRate: this.minAskingRateMo,
          minAskingUom: this.minAskingRateMoUom,
          maxAskingRate: this.maxAskingRateMo,
          maxAskingUom: this.maxAskingRateMoUom,
        }
      : {
          minAskingRate: this.minAskingRateYr,
          minAskingUom: this.minAskingRateYrUom,
          maxAskingRate: this.maxAskingRateYr,
          maxAskingUom: this.maxAskingRateYrUom,
        };
    return this.addEstExtraText(generateRateRange(rateObject, isMonthly), detailsToDisplay);
  }

  addEstExtraText(rateRange: string, detailsToDisplay: SpaceCardDetails): string {
    const rateRangeResult = detailsToDisplay?.includeEstimatedRates === this.estimatedAskingRentF ? rateRange : '-';
    if (!isNotNullOrEmptyString(rateRangeResult) || rateRangeResult === '-') return rateRangeResult;
    return rateRangeResult + (detailsToDisplay?.includeEstimatedRateIndicator ? ' (Est)' : '');
  }

  generateListingRepAgency(): string {
    const repAgency = [...new Set((this.listingReps || []).map(val => val.companyName))]
      .filter(isNotNullOrEmptyString)
      .join(', ');
    return !!repAgency.length ? repAgency : null;
  }

  /**
   * An availability is current if: Status is Active, and Date Available is past
   * @returns
   */
  isCurrentSpace(): boolean {
    const isDateListedInPast =
      this.dateOnMarket == null || moment(this.dateOnMarket, UTC_EDP_INPUT_FORMAT).isSameOrBefore(moment());
    return this.spaceAvailabilityStatus === SpaceAvailabilityStatusEnum.Active && isDateListedInPast;
  }

  isFutureSpace(): boolean {
    return (
      this.activeF && this.dateAvailable != null && moment(this.dateAvailable, UTC_EDP_INPUT_FORMAT).isAfter(moment())
    );
  }

  toString(options?: { detailsToDisplay: SpaceCardDetails; mappedUomLov?: MappedLov }): string {
    const translatedSuite = this.options?.translationService?.translate('overlayDetails.spaceLocation') || 'Suite';
    const title = isNotNullOrEmptyString(this.suiteNumber)
      ? `${translatedSuite} ${this.suiteNumber}`
      : AbstractSpaceClass.unknown;
    const tag = this.options?.translationService?.translate('commonLabels.available') || 'Available';
    const { detailsToDisplay, mappedUomLov } = options;
    const fieldsToDisplay = AbstractSpaceClass.getRequiredFieldsToDisplay(
      detailsToDisplay,
      key => key != 'leaseEndingDate'
    );
    const userPref = getUserRatePrefs();
    const fieldsData = {
      suiteNumber: sanitizeValueOr(AbstractSpaceClass.unknown, this.getSuiteNumber(this.options)),
      spaceSize: sanitizeValueOr(AbstractSpaceClass.unknown, this.generateFloorSpace(mappedUomLov || null)),
      rentDate: sanitizeValueOr(AbstractSpaceClass.unknown, this.generateRentRate(userPref)),
      tenantRepAgency: sanitizeValueOr(AbstractSpaceClass.unknown, this.generateListingRepAgency()),
    };

    return fieldsToDisplay.reduce(
      (acc, fieldKey) => (fieldsData[fieldKey] ? `${acc}\n ${fieldsData[fieldKey]}` : acc),
      `${title} - ${tag}`
    );
  }

  enableEditEvent(): boolean {
    return true;
  }

  getSpaceAvailabilityStatus() {
    return this.spaceAvailabilityStatus;
  }

  areFloorAndSuitRequiredValidation(availabilityType?: string): ValidatorFn {
    const isOfficeUsageType = this.refPropertyUsageTypeDesc.toLowerCase() === 'office';
    const isLeaseAvailType =
      (availabilityType?.toLowerCase() || this.availabilityType.toLowerCase()) === AvailabilityType.Lease.toLowerCase();
    return isOfficeUsageType && isLeaseAvailType ? Validators.required : null;
  }
  isLeaseTypeEditable(availabilityType?: AvailabilityType | ''): boolean {
    return (
      availabilityType == null ||
      availabilityType === '' ||
      [AvailabilityType.Lease, AvailabilityType.SaleOrLease, AvailabilityType.Unknown].includes(availabilityType)
    );
  }
}
